import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HUMAN_DATE_FORMAT } from '../utils/constants';
import { skipSc } from './api';
import { getIdFromGid, getNextBillingDate } from './utils';
import { useGlobalContext } from './state/GlobalContext';
import { useSkipOptions } from './state/useSkipOptions';
import { isObject } from 'lodash';
import { getBatchingNextBillingDate } from '../../utils/batching';
import dayjs from 'dayjs';
import ActionsMenu from '../components/actionsMenu';
import Button from '../components/Button';

interface IProps {
  subscription: Subscription;
  onUpdate: (msg: string) => void;
  onError: (msg: string) => void;
  isBillingOrUpdating?: boolean;
  shopConfiguration?: ShopConfiguration;
}

export default ({
  subscription,
  onUpdate,
  onError,
  isBillingOrUpdating,
  shopConfiguration,
}: IProps): React.ReactElement<IProps> => {
  const { t } = useTranslation();
  const { customerId, authToken } = useGlobalContext();
  const { skipOptions } = useSkipOptions();
  const [loading, setLoading] = useState(false);
  const isBatchedV2 =
    isObject(subscription?.BatchingConfiguration) &&
    subscription?.BatchingConfiguration['anchor'] !== '';
  const [interval, intervalNumber] =
    subscription.BillingIntervalBillingCount.split('#');
  const nextDate = isBatchedV2
    ? dayjs(
        // In order to force the calculation from the current next billing date
        // we set it as retry with that next billing date as the original
        // next billing date, so the function starts the calculations from that day
        getBatchingNextBillingDate({
          batchingConfiguration: subscription.BatchingConfiguration,
          billingCount: intervalNumber,
          billingInterval: interval,
          shopDomainOffset: shopConfiguration?.ShopDomainOffset,
          shopDomainTZ: shopConfiguration?.ShopDomainTZ,
          isRetry: true,
          originalNextBillingDate: subscription.NextBillingDate,
        }),
      )
    : getNextBillingDate(subscription);
  const nextDateDisplay = new Date(nextDate.toString()).toLocaleDateString();

  const handleClick = async (intervalId?: string) => {
    try {
      setLoading(true);
      const { nextBillingDate } = await skipSc(
        customerId,
        getIdFromGid(subscription.SubscriptionId),
        authToken,
        intervalId,
      );
      onUpdate(
        t('subscription.skip_billing_interval_success', {
          date: dayjs(nextBillingDate).format(HUMAN_DATE_FORMAT),
        }),
      );
    } catch (err) {
      console.error(err);
      onError(t('subscription.skip_billing_interval_error'));
    } finally {
      setTimeout(function () {
        setLoading(false);
      }, 3000);
    }
  };

  return skipOptions ? (
    <SkipDropdown
      t={t}
      loading={loading}
      handleClick={handleClick}
      isBillingOrUpdating={isBillingOrUpdating}
      skipOptions={skipOptions}
    />
  ) : (
    <SkipButton
      t={t}
      loading={loading}
      handleClick={() => handleClick()}
      isBillingOrUpdating={isBillingOrUpdating}
      nextDateDisplay={nextDateDisplay}
    />
  );
};

const SkipButton = ({
  t,
  loading,
  handleClick,
  isBillingOrUpdating,
  nextDateDisplay,
}) => {
  return (
    <>
      <button
        type="button"
        className="bundleapp-btn--text"
        data-bundle-tip={`${t('subscription.skip_until')} ${nextDateDisplay}`}
        disabled={loading || isBillingOrUpdating}
        onClick={handleClick}
      >
        {t('tables.skip')}
      </button>
    </>
  );
};

const SkipDropdown = ({
  t,
  loading,
  handleClick,
  isBillingOrUpdating,
  skipOptions,
}) => (
  <>
    <ActionsMenu
      isRemovable={true}
      isEditable={true}
      menuOptions={skipOptions.map((skipOption) => ({
        type: 'skip',
        text: skipOption.label,
        action: () => handleClick(skipOption.id),
      }))}
      ButtonComponent={SkipMenuButton}
      buttonDisabled={loading || isBillingOrUpdating}
      text={t('tables.skip')}
    />
  </>
);

interface ButtonComponent {
  onClick?: (e: any) => void;
  children?: string | React.ReactNode;
  ariaLabel?: string;
  className?: string;
  disabled?: boolean;
}
const SkipMenuButton = React.forwardRef(
  ({ onClick, children, disabled }: ButtonComponent, ref) => (
    <Button onClick={onClick} disabled={disabled}>
      {children}
    </Button>
  ),
);
